import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchHeatingCoolingSetPoints,
  saveHeatingCoolingSetPoints,
  getFleet,
  fetchFleetCurrentLimit,
  saveFleetCurrentLimit,
  fetchFleetControlValues,
  saveFleetControlValues,
  fetchFleetSchedule,
  saveFleetSchedule,
  deleteFleetSchedule,
} from "../lib/api";
import { useFleetStatusCheck } from "../hooks/useFleetStatusCheck";
import gotoUnits from "../assets/images/gotoUnits.png";
import useFleetStore from "../utils/store";
import { convertCelsiusToFahrenheit, convertFahrenheitToCelsius } from "../utils/common_utils";
import { DEFAULT_CONTROL_VALUES, MODE_OPTIONS, FAN_SETTING_OPTIONS, SCHEDULE_MODE_OPTIONS } from "../utils/constants";
import ToastMessage from "../components/Common/ToastMessage";
import LoadingModal from "../components/Common/LoadingModal";
import Button from "../components/Common/Button";
import { useNavigate, useLocation } from "react-router-dom";
import gotoUnitsInverse from "../assets/images/gotoUnitsInverse.png";
import ProgressBar from "../components/Common/ProgressBar";
import DisconnectedIcon from "../components/Common/DisconnectedIcon";
import CheckIcon from "../components/Common/CheckIcon";
import AlertIcon from "../components/Common/AlertIcon";
import ConfirmModal from "../components/Common/ConfirmModal";
import { CURRENT_LIMIT_DISPLAY_VALUES } from "../utils/constants";
import UnitControlMenu from "../components/Common/UnitControlMenu";
import CircularSetpointControl from "../components/Common/CircularSetpointControl";
import GenerateReportModal from "../components/Common/GenerateReportModal";
import ScheduleEventModal from "../components/Schedule/ScheduleEventModal";
import { colorStyle } from "../components/Common/ColorDropdown";
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";
import { useDeadbandSetpoints } from "../hooks/useDeadbandSetpoints";
import Icon from "../assets/icons/Icon";
import powerSymbol from "../assets/icons/power-symbol.svg";
import powerSymbolInverted from "../assets/icons/power-symbol-inverted.svg";
import {
  SCHEDULE_DAYS_ORDER,
  SCHEDULE_DAY_SHORT_NAMES,
  MAX_SCHEDULE_EVENTS,
  formatMinuteOfDayLabel,
  formatEventSummary,
} from "../utils/scheduleUtils";
import { resolveDisplaySetpoints } from "../utils/setpointUtils";
import { trackEvent } from "../lib/analytics";
import { AnalyticsSchemas, ControlType, ScheduleAction } from "../lib/analyticsEvents";

const POST_SAVE_CHECK_DELAY_MS = 3000;
const ZONE_LOAD_GATE_TIMEOUT_MS = 15000;
const SETPOINTS_UNAVAILABLE_MESSAGE =
  "This zone's temperature limits are unavailable, so setpoints cannot be saved yet.";
const DEFAULT_HOT_ALERT_THRESHOLD_F = 80;
const DEFAULT_COLD_ALERT_THRESHOLD_F = 60;
const CURRENT_LIMIT_OPTIONS = Object.keys(CURRENT_LIMIT_DISPLAY_VALUES).map((key) => ({
  value: key,
  label: CURRENT_LIMIT_DISPLAY_VALUES[key],
}));

const toFahrenheitOrNull = (celsius) =>
  celsius == null ? null : convertCelsiusToFahrenheit(celsius);

const SETPOINTS_REQUIRED_BY_MODE = {
  HEAT: ["heat"],
  COOL: ["cool"],
  DRY: ["cool"],
  HEAT_AND_COOL: ["heat", "cool"],
  FAN_ONLY: [],
};

export const hasSetpointsRequiredForMode = (mode, heatF, coolF) =>
  (SETPOINTS_REQUIRED_BY_MODE[mode] ?? []).every((setpoint) =>
    Number.isFinite(setpoint === "heat" ? heatF : coolF)
  );

export const togglePowerOnSelection = (currentPowerOn, clickedPowerOn) =>
  currentPowerOn === clickedPowerOn ? null : clickedPowerOn;

export const getMissingControlSelectionMessage = (powerOn, mode) => {
  if (powerOn === false || mode) {
    return null;
  }
  return powerOn === true ? "Please select a mode" : "Please select control values";
};

const Control = () => {
  const isAccessRestricted = useIsWriteRestrictedUser();
  const { selectedFleet, allPodData } = useFleetStore();
  const [temperatureUnit, setTemperatureUnit] = useState("°F");
  const [zoneHeatingSetPoint, setZoneHeatingSetPoint] = useState(null);
  const [zoneCoolingSetPoint, setZoneCoolingSetPoint] = useState(null);
  const [coldAlertThresholdF, setColdAlertThresholdF] = useState(null);
  const [hotAlertThresholdF, setHotAlertThresholdF] = useState(null);
  const lastColdAlertThresholdFRef = useRef(null);
  const lastHotAlertThresholdFRef = useRef(null);
  const [initialColdAlertThresholdF, setInitialColdAlertThresholdF] = useState(null);
  const [initialHotAlertThresholdF, setInitialHotAlertThresholdF] = useState(null);
  const [isHotAlertEnabled, setIsHotAlertEnabled] = useState(false);
  const [isColdAlertEnabled, setIsColdAlertEnabled] = useState(false);
  const [initialIsHotAlertEnabled, setInitialIsHotAlertEnabled] = useState(false);
  const [initialIsColdAlertEnabled, setInitialIsColdAlertEnabled] = useState(false);
  const [fallbackCoolingSetPoint, setFallbackCoolingSetPoint] = useState(null);
  const [fallbackHeatingSetPoint, setFallbackHeatingSetPoint] = useState(null);
  const [hasZoneTempChanges, setHasZoneTempChanges] = useState(false);
  const [numTotalUnits, setNumTotalUnits] = useState(0);
  const [loader, setLoader] = useState(false);
  const sliderWrapperRefHeating = useRef(null);
  const sliderWrapperRefCooling = useRef(null);
  const sliderWrapperRefHotAlert = useRef(null);
  const sliderWrapperRefColdAlert = useRef(null);
  const [numUnitAcknowledgedSetpointLimits, setNumUnitAcknowledgedSetpointLimits] = useState(0);
  const [statusCheckSummary, setStatusCheckSummary] = useState(null);
  const [currentLimit, setCurrentLimit] = useState(null);
  const [initialCurrentLimit, setInitialCurrentLimit] = useState(null);
  const [numUnitsSetCurrentLimit, setNumUnitsSetCurrentLimit] = useState(null);
  const [numUnitsSetControlValues, setNumUnitsSetControlValues] = useState(0);
  const [isDisplayingConfirmCurrentLimitModal, setIsDisplayingConfirmCurrentLimitModal] = useState(false);
  const [isDisplayingConfirmTemperatureLimitsModal, setIsDisplayingConfirmTemperatureLimitsModal] = useState(false);
  const [isDisplayingSetpointAlertWarningModal, setIsDisplayingSetpointAlertWarningModal] = useState(false);
  const [initialControlValues, setInitialControlValues] = useState(null);
  const [loadedControlValuesUnid, setLoadedControlValuesUnid] = useState(null);
  const [loadedSetpointLimitsUnid, setLoadedSetpointLimitsUnid] = useState(null);
  const [unblockedZoneUnid, setUnblockedZoneUnid] = useState(null);
  const [selectedMode, setSelectedMode] = useState();
  const [selectedFan, setSelectedFan] = useState();
  const [isControlValuesEnabled, setControlValuesEnabled] = useState(false);
  const controlValuesEnabledOnServerRef = useRef(false);
  const [powerOn, setPowerOn] = useState(null);
  const [heatTargetTempF, setHeatTargetTempF] = useState();
  const [coolTargetTempF, setCoolTargetTempF] = useState();
  const [hasControlChanges, setHasControlChanges] = useState(false);
  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(false);
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [numUnitsSetSchedule, setNumUnitsSetSchedule] = useState(0);
  const [isScheduleEventModalOpen, setIsScheduleEventModalOpen] = useState(false);
  const [editingScheduleEvent, setEditingScheduleEvent] = useState(null);
  const [isSavingScheduleEvent, setIsSavingScheduleEvent] = useState(false);
  const [isDisplayingClearScheduleModal, setIsDisplayingClearScheduleModal] = useState(false);

  const navigate = useNavigate();
  const postSetpointLimitsSaveTimeoutRef = useRef();
  const postCurrentLimitSaveTimeoutRef = useRef();
  const postControlValuesSaveTimeoutRef = useRef();
  const postScheduleSaveTimeoutRef = useRef();
  const selectedUnidRef = useRef(selectedFleet?.unid);
  selectedUnidRef.current = selectedFleet?.unid;
  const location = useLocation();
  const areSetpointLimitsForSelectedZone =
    !!selectedFleet?.unid && loadedSetpointLimitsUnid === selectedFleet.unid;
  const areControlValuesForSelectedZone =
    !!selectedFleet?.unid && loadedControlValuesUnid === selectedFleet.unid;
  // The page must not render another zone's values under the newly selected zone's
  // name, so it stays behind the loader until this zone's own values arrive. Having no
  // zone, a failed fetch and a request that never settles all release it: LoadingModal
  // covers the whole viewport, so a gate that cannot open locks the user out of the app.
  const isSelectedZoneDataReady =
    !selectedFleet?.unid ||
    (areControlValuesForSelectedZone && areSetpointLimitsForSelectedZone) ||
    unblockedZoneUnid === selectedFleet.unid;
  const isZoneTempSaveDisabled =
    isAccessRestricted || !hasZoneTempChanges || !areSetpointLimitsForSelectedZone;
  const isControlToggleDisabled = isAccessRestricted || !areControlValuesForSelectedZone;
  const isZonePoweredOff = powerOn === false;
  const isForceResetDisabled =
    isAccessRestricted ||
    !areControlValuesForSelectedZone ||
    !areSetpointLimitsForSelectedZone ||
    !(isControlValuesEnabled && !hasControlChanges && (isZonePoweredOff || selectedMode !== null));
  const isControlSaveDisabled =
    isAccessRestricted ||
    !areControlValuesForSelectedZone ||
    !areSetpointLimitsForSelectedZone ||
    !(isControlValuesEnabled && hasControlChanges);
  const areSettingsDisabled = !isControlValuesEnabled || isZonePoweredOff;
  const isCurrentLimitSaveDisabled =
    isAccessRestricted || !currentLimit || currentLimit === initialCurrentLimit;
  const isScheduleToggleDisabled = isAccessRestricted;
  const isAddScheduleEventDisabled = isAccessRestricted || scheduleEvents.length >= MAX_SCHEDULE_EVENTS;
  const isScheduleForceResetDisabled =
    isAccessRestricted || !isScheduleEnabled || scheduleEvents.length === 0;

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
  }, []);

  const allowedRangesF = {
    heatUpperLimit: { min: 55, max: 85 },
    coolLowerLimit: { min: 60, max: 90 },
    coldAlertThreshold: { min: 50, max: 85 },
    hotAlertThreshold: { min: 60, max: 95 },
  };
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const setHotAlertThresholdValue = useCallback((value, { preserveLastValue = false } = {}) => {
    if (value === null || value === undefined) {
      if (!preserveLastValue) {
        lastHotAlertThresholdFRef.current = null;
      }
    } else {
      lastHotAlertThresholdFRef.current = value;
    }
    setHotAlertThresholdF(value);
  }, []);

  const setColdAlertThresholdValue = useCallback((value, { preserveLastValue = false } = {}) => {
    if (value === null || value === undefined) {
      if (!preserveLastValue) {
        lastColdAlertThresholdFRef.current = null;
      }
    } else {
      lastColdAlertThresholdFRef.current = value;
    }
    setColdAlertThresholdF(value);
  }, []);

  const handleNavigateToUnits = () => {
    navigate("/manageUnits");
  };

  const handleGenerateReport = () => {
    setIsGenerateReportModalOpen(true);
  };

  const handleHotAlertThresholdToggle = () => {
    if (isAccessRestricted) return;
    if (isHotAlertEnabled) {
      setIsHotAlertEnabled(false);
      setHotAlertThresholdValue(null, { preserveLastValue: true });
      return;
    }
    setIsHotAlertEnabled(true);
    setHotAlertThresholdValue(lastHotAlertThresholdFRef.current ?? DEFAULT_HOT_ALERT_THRESHOLD_F);
  };

  const handleColdAlertThresholdToggle = () => {
    if (isAccessRestricted) return;
    if (isColdAlertEnabled) {
      setIsColdAlertEnabled(false);
      setColdAlertThresholdValue(null, { preserveLastValue: true });
      return;
    }
    setIsColdAlertEnabled(true);
    setColdAlertThresholdValue(lastColdAlertThresholdFRef.current ?? DEFAULT_COLD_ALERT_THRESHOLD_F);
  };

  const disconnectedCount = statusCheckSummary?.numDisconnectedUnits || 0;

  const fetchSetpointLimitData = useCallback(async ({ shouldSetSelectedSetpointLimits = false }) => {
    const payload = { unid: selectedFleet.unid };
    try {
      const response = await fetchHeatingCoolingSetPoints(payload);

      // A response that lost the race against a zone switch must not be written to
      // state, or it would re-tag the page with the zone the user has already left.
      if (payload.unid !== selectedUnidRef.current) {
        return;
      }

      setNumUnitAcknowledgedSetpointLimits(response?.data?.status?.numUnitsSet ?? 0);
      // Always set the zone limits for fallback use
      const heatLimitF = toFahrenheitOrNull(response?.data?.limits?.heatUpperLimitC);
      const coolLimitF = toFahrenheitOrNull(response?.data?.limits?.coolLowerLimitC);
      const coldAlertC = response?.data?.limits?.coldAlertThresholdC;
      const hotAlertC = response?.data?.limits?.hotAlertThresholdC;
      if (shouldSetSelectedSetpointLimits) {
        const coldAlertF =
          coldAlertC === null || coldAlertC === undefined
            ? null
            : convertCelsiusToFahrenheit(coldAlertC);
        const hotAlertF =
          hotAlertC === null || hotAlertC === undefined
            ? null
            : convertCelsiusToFahrenheit(hotAlertC);
        const hotEnabled = hotAlertF !== null;
        const coldEnabled = coldAlertF !== null;
        setZoneHeatingSetPoint(heatLimitF);
        setZoneCoolingSetPoint(coolLimitF);
        setColdAlertThresholdValue(coldAlertF);
        setHotAlertThresholdValue(hotAlertF);
        setInitialColdAlertThresholdF(coldAlertF);
        setInitialHotAlertThresholdF(hotAlertF);
        setIsHotAlertEnabled(hotEnabled);
        setIsColdAlertEnabled(coldEnabled);
        setInitialIsHotAlertEnabled(hotEnabled);
        setInitialIsColdAlertEnabled(coldEnabled);
        setFallbackCoolingSetPoint(coolLimitF);
        setFallbackHeatingSetPoint(heatLimitF);
        setLoadedSetpointLimitsUnid(payload.unid);
      }
    } catch (error) {
      // Only the initial load gates the page, so a failing background poll must not
      // mark the zone as failed and release the gate for everything that follows.
      if (shouldSetSelectedSetpointLimits) {
        setUnblockedZoneUnid(payload.unid);
      }
      showToast("Failed to fetch Heating/Cooling Set-points", "failed");
      console.error("Error fetching Heating/Cooling Set-points:", error);
    }
  }, [selectedFleet?.unid, setColdAlertThresholdValue, setHotAlertThresholdValue, showToast]);

  const loadCurrentLimitData = useCallback(async ({
    shouldSetSelectedCurrentLimit = false,
    shouldSetInitialCurrentLimit = false,
  }) => {
    if (!selectedFleet?.unid) {
      return;
    }
    const unid = selectedFleet.unid;
    try {
      const response = await fetchFleetCurrentLimit({ unid });
      if (unid !== selectedUnidRef.current) {
        return;
      }
      if (shouldSetSelectedCurrentLimit) {
        setCurrentLimit(response?.data?.currentLimit);
      }
      if (shouldSetInitialCurrentLimit) {
        setInitialCurrentLimit(response?.data?.currentLimit);
      }
      setNumUnitsSetCurrentLimit(response?.data?.status?.numUnitsSet);
    } catch (error) {
      showToast("Failed to fetch current limit", "failed");
      console.error("Error fetching current limit:", error);
    }
  }, [selectedFleet?.unid, showToast]);

  const loadScheduleData = useCallback(async ({ shouldSetSelectedSchedule = false } = {}) => {
    if (!selectedFleet?.unid) {
      return;
    }
    const unid = selectedFleet.unid;
    try {
      const response = await fetchFleetSchedule({ unid });
      if (unid !== selectedUnidRef.current) {
        return;
      }
      if (shouldSetSelectedSchedule) {
        setIsScheduleEnabled(Boolean(response?.data?.schedule?.isScheduleEnabled));
        setScheduleEvents(response?.data?.schedule?.events ?? []);
      }
      setNumUnitsSetSchedule(response?.data?.status?.numUnitsSet ?? 0);
    } catch (error) {
      showToast("Failed to fetch fleet schedule", "failed");
      console.error("Error fetching fleet schedule:", error);
    }
  }, [selectedFleet?.unid, showToast]);

  const getFleetControlValues = useCallback(async ({
    shouldSetSelectedControlValues = false,
    shouldSetInitialControlValues = false,
  }) => {
    const payload = { unid: selectedFleet.unid };
    try {
      const data = await fetchFleetControlValues(payload);
      if (payload.unid !== selectedUnidRef.current) {
        return;
      }
      if (shouldSetSelectedControlValues) {
        controlValuesEnabledOnServerRef.current = Boolean(
          data?.data?.controlValues?.isControlValuesEnabled
        );
        setControlValuesEnabled(data?.data?.controlValues?.isControlValuesEnabled);
        setPowerOn(data?.data?.controlValues?.powerOn ?? null);
        setSelectedMode(data?.data?.controlValues?.mode);
        setSelectedFan(data?.data?.controlValues?.fanSetting);
        const coolC = data?.data?.controlValues?.coolTargetTempC;
        const heatC = data?.data?.controlValues?.heatTargetTempC;
        setCoolTargetTempF(
          coolC === null || coolC === undefined
            ? null
            : convertCelsiusToFahrenheit(coolC)
        );
        setHeatTargetTempF(
          heatC === null || heatC === undefined
            ? null
            : convertCelsiusToFahrenheit(heatC)
        );
        setLoadedControlValuesUnid(payload.unid);
      }
      setNumUnitsSetControlValues(data?.data?.status?.numUnitsSet);
      if (shouldSetInitialControlValues) {
        const cv = data?.data?.controlValues;

        setInitialControlValues({
          powerOn: cv.powerOn ?? null,
          mode: cv.mode,
          fanSetting: cv.fanSetting,
          heatTargetTempF:
            cv.heatTargetTempC == null
              ? fallbackHeatingSetPoint
              : convertCelsiusToFahrenheit(cv.heatTargetTempC),
          coolTargetTempF:
            cv.coolTargetTempC == null
              ? fallbackCoolingSetPoint
              : convertCelsiusToFahrenheit(cv.coolTargetTempC),
        });
      }
    } catch (error) {
      if (shouldSetSelectedControlValues) {
        setUnblockedZoneUnid(payload.unid);
      }
      console.error("Error fetching the fleet control values:", error);
      showToast("Failed to fetch the fleet control values", "failed");
    }
  }, [selectedFleet?.unid, showToast, fallbackCoolingSetPoint, fallbackHeatingSetPoint]);

  const handleSliderChange = (e, ref, setter, type) => {
    const rect = ref.current.getBoundingClientRect();

    let clientX;
    if (e.type.startsWith("touch")) {
      // For touch events
      clientX = e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX;
    } else {
      // For mouse events
      clientX = e.clientX;
    }

    const percent = Math.max(
      0,
      Math.min(100, ((clientX - rect.left) / rect.width) * 100)
    );

    const range = allowedRangesF[type];
    const value = range.min + (range.max - range.min) * (percent / 100);

    setter(parseFloat(value.toFixed(2)));
  };

  const startDrag = (e, ref, setter, type) => {
    const move = (ev) => handleSliderChange(ev, ref, setter, type);
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };

    // Call on initial drag start
    handleSliderChange(e, ref, setter, type);

    // Mouse Events
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);

    // Touch Events
    window.addEventListener('touchmove', move);
    window.addEventListener('touchend', up);
  };

  const getSliderPercentFromFahrenheit = (value, type) => {
    const range = allowedRangesF[type];
    return ((value - range.min) / (range.max - range.min)) * 100;
  };
  const getThresholdPercentFromFahrenheit = (value, type) =>
    value === null ? 0 : getSliderPercentFromFahrenheit(value, type);
  const getThresholdMarkerLeft = (percent) =>
    percent <= 0 ? "0px" : `calc(${percent}% - 12px)`;

  const saveCurrentLimitSelection = useCallback(async () => {
    if (!selectedFleet?.unid || !currentLimit || currentLimit === initialCurrentLimit) {
      return;
    }
    if (postCurrentLimitSaveTimeoutRef.current) {
      clearTimeout(postCurrentLimitSaveTimeoutRef.current);
    }
    try {
      await saveFleetCurrentLimit({
        unid: selectedFleet.unid,
        currentLimit,
      });
      setInitialCurrentLimit(currentLimit);
      trackEvent(AnalyticsSchemas.webDeviceControl, {
        unid: selectedFleet.unid,
        control_type: ControlType.currentLimitChange,
        current_limit: currentLimit,
      });
      showToast("Current limit updated successfully!", "success");
      setNumUnitsSetCurrentLimit(0);
      // check number of units that acknowledged the current limit after a small delay
      postCurrentLimitSaveTimeoutRef.current = setTimeout(() => {
        loadCurrentLimitData({
          shouldSetSelectedCurrentLimit: false,
          shouldSetInitialCurrentLimit: false,
        });
      }, POST_SAVE_CHECK_DELAY_MS);
    } catch (error) {
      showToast("Failed to update current limit", "failed");
      console.error("Error saving current limit:", error);
    }
  }, [selectedFleet?.unid, currentLimit, initialCurrentLimit, loadCurrentLimitData, showToast]);

  const saveSchedule = useCallback(async (nextSchedule) => {
    if (!selectedFleet?.unid) {
      return;
    }
    const unid = selectedFleet.unid;
    if (postScheduleSaveTimeoutRef.current) {
      clearTimeout(postScheduleSaveTimeoutRef.current);
    }
    try {
      const response = await saveFleetSchedule({ unid, schedule: nextSchedule });
      if (unid !== selectedUnidRef.current) {
        return;
      }
      setIsScheduleEnabled(Boolean(response?.data?.schedule?.isScheduleEnabled));
      setScheduleEvents(response?.data?.schedule?.events ?? []);
      setNumUnitsSetSchedule(0);
      postScheduleSaveTimeoutRef.current = setTimeout(() => {
        loadScheduleData({ shouldSetSelectedSchedule: false });
      }, POST_SAVE_CHECK_DELAY_MS);
    } catch (error) {
      showToast("Failed to save fleet schedule", "failed");
      console.error("Error saving fleet schedule:", error);
      throw error;
    }
  }, [selectedFleet?.unid, loadScheduleData, showToast]);

  const handleScheduleToggle = () => {
    if (isScheduleToggleDisabled) return;
    const nextEnabled = !isScheduleEnabled;
    if (nextEnabled && scheduleEvents.length === 0) {
      showToast("Add at least one event before turning the schedule on.", "failed");
      return;
    }
    const unid = selectedFleet.unid;
    saveSchedule({ isScheduleEnabled: nextEnabled, events: scheduleEvents })
      .then(() => {
        trackEvent(AnalyticsSchemas.webScheduleEvent, {
          unid,
          schedule_action: nextEnabled ? ScheduleAction.globalEnable : ScheduleAction.globalDisable,
        });
        showToast(`Schedule turned ${nextEnabled ? "on" : "off"}!`, "success");
      })
      .catch(() => {});
  };

  const openCreateScheduleEventModal = () => {
    setEditingScheduleEvent(null);
    setIsScheduleEventModalOpen(true);
  };

  const openEditScheduleEventModal = (event, index) => {
    setEditingScheduleEvent({ event, index });
    setIsScheduleEventModalOpen(true);
  };

  const closeScheduleEventModal = () => {
    setIsScheduleEventModalOpen(false);
    setEditingScheduleEvent(null);
  };

  const handleSaveScheduleEvent = async (eventValues) => {
    if (!selectedFleet?.unid) return;
    const unid = selectedFleet.unid;
    const nextEvents = [...scheduleEvents];
    if (editingScheduleEvent) {
      nextEvents[editingScheduleEvent.index] = eventValues;
    } else {
      nextEvents.push(eventValues);
    }
    setIsSavingScheduleEvent(true);
    try {
      // Saving an event only takes effect when isScheduleEnabled is true - the API ignores the
      // events array entirely on a request that disables the schedule.
      await saveSchedule({ isScheduleEnabled: true, events: nextEvents });
      trackEvent(AnalyticsSchemas.webScheduleEvent, {
        unid,
        schedule_action: editingScheduleEvent ? ScheduleAction.update : ScheduleAction.create,
        time: eventValues.minuteOfDay ?? null,
        days: eventValues.daysOfWeek?.length ? eventValues.daysOfWeek.join(",") : null,
        op_mode: eventValues.eventType ?? null,
        fan_setting: eventValues.fanSetting ?? null,
        heat_target_temp_c: eventValues.heatTargetTempC ?? null,
        cool_target_temp_c: eventValues.coolTargetTempC ?? null,
      });
      if (unid !== selectedUnidRef.current) {
        return;
      }
      showToast("Schedule event saved!", "success");
      closeScheduleEventModal();
    } catch (error) {
      // error toast already shown by saveSchedule
    } finally {
      setIsSavingScheduleEvent(false);
    }
  };

  const clearScheduleFromServer = useCallback(async () => {
    if (!selectedFleet?.unid) return;
    const unid = selectedFleet.unid;
    const response = await deleteFleetSchedule({ unid });
    if (unid !== selectedUnidRef.current) {
      return;
    }
    setIsScheduleEnabled(Boolean(response?.data?.schedule?.isScheduleEnabled));
    setScheduleEvents(response?.data?.schedule?.events ?? []);
    setNumUnitsSetSchedule(0);
  }, [selectedFleet?.unid]);

  const handleDeleteScheduleEvent = async () => {
    if (!editingScheduleEvent || !selectedFleet?.unid) return;
    const unid = selectedFleet.unid;
    const isLastRemainingEvent = scheduleEvents.length === 1;
    setIsSavingScheduleEvent(true);
    try {
      if (isLastRemainingEvent) {
        // Deleting the last event clears the whole schedule server-side, same as Clear Schedule.
        await clearScheduleFromServer();
      } else {
        const nextEvents = scheduleEvents.filter((_, index) => index !== editingScheduleEvent.index);
        await saveSchedule({ isScheduleEnabled: true, events: nextEvents });
      }
      trackEvent(AnalyticsSchemas.webScheduleEvent, {
        unid,
        schedule_action: isLastRemainingEvent ? ScheduleAction.deleteAll : ScheduleAction.delete,
      });
      if (unid !== selectedUnidRef.current) {
        return;
      }
      showToast("Schedule event deleted!", "success");
      closeScheduleEventModal();
    } catch (error) {
      if (isLastRemainingEvent) {
        showToast("Failed to delete the schedule event", "failed");
        console.error("Error deleting fleet schedule:", error);
      }
      // else: error toast already shown by saveSchedule
    } finally {
      setIsSavingScheduleEvent(false);
    }
  };

  const handleClearSchedule = async () => {
    const unid = selectedFleet.unid;
    try {
      await clearScheduleFromServer();
      trackEvent(AnalyticsSchemas.webScheduleEvent, {
        unid,
        schedule_action: ScheduleAction.deleteAll,
      });
      showToast("Schedule cleared!", "success");
    } catch (error) {
      showToast("Failed to clear the schedule", "failed");
      console.error("Error clearing fleet schedule:", error);
    } finally {
      setIsDisplayingClearScheduleModal(false);
    }
  };

  // Re-sends the current, unchanged schedule to force every unit to re-acknowledge it - only
  // meaningful while the schedule is enabled, since a disabled request takes the clear-from-devices
  // branch on the backend instead of re-pushing.
  const handleForceResetSchedule = async () => {
    if (isScheduleForceResetDisabled) return;
    try {
      await saveSchedule({ isScheduleEnabled: true, events: scheduleEvents });
      showToast("Schedule force reset is successful", "success");
    } catch (error) {
      // error toast already shown by saveSchedule
    }
  };

  const isOutOfRange = (value, range) => value < range.min || value > range.max;

  const getSetpointAlertWarningMessages = useCallback(({
    heatUpperLimitF,
    coolLowerLimitF,
    coldAlertThresholdF: nextColdAlertThresholdF,
    hotAlertThresholdF: nextHotAlertThresholdF,
  }) => {
    const warningMessages = [];

    if (
      isHotAlertEnabled &&
      nextHotAlertThresholdF !== null &&
      nextHotAlertThresholdF !== undefined &&
      coolLowerLimitF > nextHotAlertThresholdF
    ) {
      warningMessages.push(
        "Your alert threshold is lower than your cooling setpoint limit. Units will not be able to cool down to that temperature."
      );
    }

    if (
      isColdAlertEnabled &&
      nextColdAlertThresholdF !== null &&
      nextColdAlertThresholdF !== undefined &&
      nextColdAlertThresholdF > heatUpperLimitF
    ) {
      warningMessages.push(
        "Your alert threshold is higher than your heating setpoint limit. Units will not be able to heat up to that temperature."
      );
    }

    return warningMessages;
  }, [isColdAlertEnabled, isHotAlertEnabled]);

  const updateHeatingCoolingSetPoints = async ({
    heatUpperLimitF,
    coolLowerLimitF,
    coldAlertThresholdF: nextColdAlertThresholdF,
    hotAlertThresholdF: nextHotAlertThresholdF,
    unid,
    userConfirmedSetPointsChange,
    userConfirmedAlertThresholdWarning = false,
  }) => {
    if (
      isOutOfRange(zoneHeatingSetPoint, allowedRangesF.heatUpperLimit) ||
      isOutOfRange(zoneCoolingSetPoint, allowedRangesF.coolLowerLimit)
    ) {
      showToast("Set-point values are out of range.", "failed");
      return;
    }
    if (
      (isColdAlertEnabled &&
        nextColdAlertThresholdF !== null &&
        isOutOfRange(nextColdAlertThresholdF, allowedRangesF.coldAlertThreshold)) ||
      (isHotAlertEnabled &&
        nextHotAlertThresholdF !== null &&
        isOutOfRange(nextHotAlertThresholdF, allowedRangesF.hotAlertThreshold))
    ) {
      showToast("Alert threshold values are out of range.", "failed");
      return;
    }
    const setpointAlertWarningMessages = getSetpointAlertWarningMessages({
      heatUpperLimitF,
      coolLowerLimitF,
      coldAlertThresholdF: nextColdAlertThresholdF,
      hotAlertThresholdF: nextHotAlertThresholdF,
    });

    if (!userConfirmedAlertThresholdWarning && setpointAlertWarningMessages.length > 0) {
      setIsDisplayingSetpointAlertWarningModal(true);
      return;
    }

    // If the new limits would restrict the device control sliders, show confirmation modal
    const isHeatCollision =
      heatUpperLimitF < heatTargetTempF ||
      heatUpperLimitF < DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F;
    const isCoolCollision =
      coolLowerLimitF > coolTargetTempF ||
      coolLowerLimitF > DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F;

    if (isControlValuesEnabled && !userConfirmedSetPointsChange && (isHeatCollision || isCoolCollision)) {
      setIsDisplayingConfirmTemperatureLimitsModal(true);
      return;
    }
    if (postSetpointLimitsSaveTimeoutRef.current) {
      clearTimeout(postSetpointLimitsSaveTimeoutRef.current);
    }
    const coldAlertThresholdC =
      isColdAlertEnabled && nextColdAlertThresholdF != null
        ? convertFahrenheitToCelsius(nextColdAlertThresholdF)
        : null;
    const hotAlertThresholdC =
      isHotAlertEnabled && nextHotAlertThresholdF != null
        ? convertFahrenheitToCelsius(nextHotAlertThresholdF)
        : null;

    let result;
    try {
      result = await saveHeatingCoolingSetPoints({
        unid,
        heatUpperLimitC: convertFahrenheitToCelsius(heatUpperLimitF),
        coolLowerLimitC: convertFahrenheitToCelsius(coolLowerLimitF),
        coldAlertThresholdC,
        hotAlertThresholdC,
      });
    } catch (error) {
      console.error("Error updating heating/cooling set-points:", error);
      showToast("Failed to update set-points", "failed");
      return;
    }

    if (result?.data) {
      setHasZoneTempChanges(false);
      trackEvent(AnalyticsSchemas.webDeviceControl, {
        unid,
        control_type: ControlType.setpointLimitChange,
        heat_upper_limit_c: convertFahrenheitToCelsius(heatUpperLimitF),
        cool_lower_limit_c: convertFahrenheitToCelsius(coolLowerLimitF),
      });
      showToast("Set-points updated successfully!", "success");
      setNumUnitAcknowledgedSetpointLimits(0);
      setFallbackHeatingSetPoint(heatUpperLimitF);
      setInitialColdAlertThresholdF(nextColdAlertThresholdF);
      setInitialHotAlertThresholdF(nextHotAlertThresholdF);
      setInitialIsHotAlertEnabled(isHotAlertEnabled);
      setInitialIsColdAlertEnabled(isColdAlertEnabled);
      safeSetHeatTargetTempF((prev) =>
        prev == null ? heatUpperLimitF : Math.min(prev, heatUpperLimitF)
      );

      setFallbackCoolingSetPoint(coolLowerLimitF);
      safeSetCoolTargetTempF((prev) =>
        prev == null ? coolLowerLimitF : Math.max(prev, coolLowerLimitF)
      );

      if (userConfirmedSetPointsChange) {
        UpdateFleetControlValuesAfterTemperatureLimitChange({
          heatUpperLimitF,
          coolLowerLimitF,
        });
      }

      // check the number of units that acknowledge setpoint limits after a small delay
      postSetpointLimitsSaveTimeoutRef.current = setTimeout(() => {
        fetchSetpointLimitData({ shouldSetSelectedSetpointLimits: false });
      }, POST_SAVE_CHECK_DELAY_MS);
    }
  };

  const UpdateFleetControlValuesAfterTemperatureLimitChange = async ({
    heatUpperLimitF,
    coolLowerLimitF,
  }) => {
    const payload = buildControlPayload();
    if (!isZonePoweredOff) {
      applyModeAndFan(payload);
      applySetpoints(payload, heatUpperLimitF, coolLowerLimitF);
    }

    const finalHeatF =
      payload.controlValues.heatTargetTempC != null
        ? convertCelsiusToFahrenheit(payload.controlValues.heatTargetTempC)
        : heatTargetTempF;

    const finalCoolF =
      payload.controlValues.coolTargetTempC != null
        ? convertCelsiusToFahrenheit(payload.controlValues.coolTargetTempC)
        : coolTargetTempF;

    safeSetHeatTargetTempF(finalHeatF);
    safeSetCoolTargetTempF(finalCoolF);

    setFallbackHeatingSetPoint(heatUpperLimitF);
    setFallbackCoolingSetPoint(coolLowerLimitF);

    if (postControlValuesSaveTimeoutRef.current) {
      clearTimeout(postControlValuesSaveTimeoutRef.current);
    }
    try {
      await saveFleetControlValues(payload);
      showToast("Control values updated to comply with new set-point limits", "success");
      setNumUnitsSetControlValues(0);
      setInitialControlValues({
        powerOn: payload.controlValues.powerOn,
        mode: payload.controlValues.mode,
        fanSetting: payload.controlValues.fanSetting,
        heatTargetTempF: finalHeatF,
        coolTargetTempF: finalCoolF,
      });
      postControlValuesSaveTimeoutRef.current = setTimeout(() => {
        getFleetControlValues({
          shouldSetSelectedControlValues: false,
          shouldSetInitialControlValues: false,
        });
      }, POST_SAVE_CHECK_DELAY_MS);
      setHasControlChanges(false);
      resetLastAdjustedRangeBar();

    } catch (error) {
      console.error("Error updating fleet control values after setpoint change:", error);
      showToast("Failed to update control values after set-point change", "failed");
    }

  };

  const handleControlValuesToggle = async () => {
    const newValue = !isControlValuesEnabled;
    const wasEnabledOnServer = controlValuesEnabledOnServerRef.current;
    setControlValuesEnabled(newValue);

    if (!newValue) {
      // If disabling, call API immediately
      try {
        await saveFleetControlValues({
          unid: selectedFleet.unid,
          controlValues: { isControlValuesEnabled: false },
        });
        setNumUnitsSetControlValues(0);
        setPowerOn(null);
        setSelectedMode(null);
        setSelectedFan(null);
        setCoolTargetTempF(null);
        setHeatTargetTempF(null);
        setHasControlChanges(false);
        resetLastAdjustedRangeBar();
        controlValuesEnabledOnServerRef.current = false;
        if (wasEnabledOnServer) {
          trackEvent(AnalyticsSchemas.webDeviceControl, {
            unid: selectedFleet.unid,
            control_type: ControlType.controlValuesToggle,
            enabled: false,
          });
        }
        showToast("Control values disabled!", "success");
      } catch (error) {
        showToast("Failed to disable control values", "failed");
      }
    } else {
      setHasControlChanges(true);
    }
  };

  const refreshControlData = useCallback(async ({
    shouldSetSelectedCurrentLimit = false,
    shouldSetInitialCurrentLimit = false,
    shouldSetSelectedSetpointLimits = false,
    shouldSetInitialControlValues = false,
    shouldSetSelectedControlValues = false,
    shouldSetSelectedSchedule = false,
  }) => {
    await Promise.all([
      fetchSetpointLimitData({ shouldSetSelectedSetpointLimits }),
      loadCurrentLimitData({
        shouldSetSelectedCurrentLimit,
        shouldSetInitialCurrentLimit,
      }),
      getFleetControlValues({
        shouldSetSelectedControlValues,
        shouldSetInitialControlValues,
      }),
      loadScheduleData({ shouldSetSelectedSchedule }),
    ]);
  }, [fetchSetpointLimitData, getFleetControlValues, loadCurrentLimitData, loadScheduleData]);

  useFleetStatusCheck({
    unid: selectedFleet?.unid,
    isActive: location.pathname === "/control",
    onComplete: async (summary) => {
      setStatusCheckSummary(summary);
      await refreshControlData({});
    },
  });

  const { safeSetHeatTargetTempF, safeSetCoolTargetTempF, resetLastAdjustedRangeBar } = useDeadbandSetpoints({
    mode: selectedMode,
    heatTargetTempF,
    coolTargetTempF,
    setHeatTargetTempF,
    setCoolTargetTempF,
    heatMaxF: fallbackHeatingSetPoint,
    coolMinF: fallbackCoolingSetPoint,
  });

  useEffect(() => {
    const hasHeatChanged = zoneHeatingSetPoint !== fallbackHeatingSetPoint;
    const hasCoolChanged = zoneCoolingSetPoint !== fallbackCoolingSetPoint;
    const hasColdAlertChanged = coldAlertThresholdF !== initialColdAlertThresholdF;
    const hasHotAlertChanged = hotAlertThresholdF !== initialHotAlertThresholdF;
    const hasHotToggleChanged = isHotAlertEnabled !== initialIsHotAlertEnabled;
    const hasColdToggleChanged = isColdAlertEnabled !== initialIsColdAlertEnabled;
    const isHotOffToOnWithoutValue =
      !initialIsHotAlertEnabled && isHotAlertEnabled && hotAlertThresholdF === null;
    const isColdOffToOnWithoutValue =
      !initialIsColdAlertEnabled && isColdAlertEnabled && coldAlertThresholdF === null;
    const hasMeaningfulHotToggleChange = hasHotToggleChanged && !isHotOffToOnWithoutValue;
    const hasMeaningfulColdToggleChange = hasColdToggleChanged && !isColdOffToOnWithoutValue;

    setHasZoneTempChanges(
      hasHeatChanged ||
      hasCoolChanged ||
      hasColdAlertChanged ||
      hasHotAlertChanged ||
      hasMeaningfulHotToggleChange ||
      hasMeaningfulColdToggleChange
    );
  }, [
    zoneHeatingSetPoint,
    zoneCoolingSetPoint,
    coldAlertThresholdF,
    hotAlertThresholdF,
    isHotAlertEnabled,
    isColdAlertEnabled,
    fallbackHeatingSetPoint,
    fallbackCoolingSetPoint,
    initialColdAlertThresholdF,
    initialHotAlertThresholdF,
    initialIsHotAlertEnabled,
    initialIsColdAlertEnabled,
  ]);

  const hotAlertThresholdPercent = getThresholdPercentFromFahrenheit(
    hotAlertThresholdF,
    "hotAlertThreshold"
  );
  const coldAlertThresholdPercent = getThresholdPercentFromFahrenheit(
    coldAlertThresholdF,
    "coldAlertThreshold"
  );

  // Compare current control fields to initial values whenever any control changes
  useEffect(() => {
    if (!isControlValuesEnabled || !initialControlValues) {
      setHasControlChanges(false);
      return;
    }

    const hasPowerChanged = powerOn !== initialControlValues.powerOn;

    if (isZonePoweredOff) {
      setHasControlChanges(hasPowerChanged);
      return;
    }

    const compareValues = {
      mode: selectedMode,
      fanSetting: selectedFan,
      heatTargetTempF,
      coolTargetTempF,
    };

    const fieldsToCompare = {
      HEAT: ["mode", "fanSetting", "heatTargetTempF"],
      COOL: ["mode", "fanSetting", "coolTargetTempF"],
      DRY: ["mode", "fanSetting", "coolTargetTempF"],
      HEAT_AND_COOL: ["mode", "fanSetting", "heatTargetTempF", "coolTargetTempF"],
      FAN_ONLY: ["mode", "fanSetting"],
    };

    const fields = fieldsToCompare[selectedMode] || [];

    const changed = fields.some(
      (key) => compareValues[key] !== initialControlValues[key]
    );

    setHasControlChanges(hasPowerChanged || changed);
  }, [
    selectedMode,
    selectedFan,
    heatTargetTempF,
    coolTargetTempF,
    powerOn,
    isZonePoweredOff,
    initialControlValues,
    isControlValuesEnabled,
    hasControlChanges,
  ]);

  // Drop every zone-scoped value on a switch. The readiness gate only waits on the
  // control values and setpoint limits, so anything left here would render under the
  // newly selected zone's name.
  useEffect(() => {
    setUnblockedZoneUnid(null);
    setControlValuesEnabled(false);
    setPowerOn(null);
    setSelectedMode(undefined);
    setSelectedFan(undefined);
    setHeatTargetTempF(null);
    setCoolTargetTempF(null);
    setFallbackHeatingSetPoint(null);
    setFallbackCoolingSetPoint(null);
    setZoneHeatingSetPoint(null);
    setZoneCoolingSetPoint(null);
    setInitialControlValues(null);
    setHasControlChanges(false);
    setCurrentLimit(null);
    setInitialCurrentLimit(null);
    setNumUnitsSetCurrentLimit(null);
    setNumUnitsSetControlValues(0);
    setNumUnitAcknowledgedSetpointLimits(0);
    setStatusCheckSummary(null);
    setNumTotalUnits(0);
    setHotAlertThresholdValue(null);
    setColdAlertThresholdValue(null);
    setInitialHotAlertThresholdF(null);
    setInitialColdAlertThresholdF(null);
    setIsHotAlertEnabled(false);
    setIsColdAlertEnabled(false);
    setInitialIsHotAlertEnabled(false);
    setInitialIsColdAlertEnabled(false);
    resetLastAdjustedRangeBar();
    setIsDisplayingConfirmCurrentLimitModal(false);
    setIsDisplayingConfirmTemperatureLimitsModal(false);
    setIsDisplayingSetpointAlertWarningModal(false);
    setIsScheduleEnabled(false);
    setScheduleEvents([]);
    setNumUnitsSetSchedule(0);
    setIsScheduleEventModalOpen(false);
    setEditingScheduleEvent(null);
    setIsDisplayingClearScheduleModal(false);
  }, [selectedFleet?.unid, setHotAlertThresholdValue, setColdAlertThresholdValue, resetLastAdjustedRangeBar]);

  // A request that hangs rather than rejecting would otherwise leave the full-viewport
  // loader up forever. The save actions stay disabled either way.
  useEffect(() => {
    const unid = selectedFleet?.unid;
    if (!unid) {
      return undefined;
    }
    const timeoutId = setTimeout(
      () => setUnblockedZoneUnid(unid),
      ZONE_LOAD_GATE_TIMEOUT_MS
    );
    return () => clearTimeout(timeoutId);
  }, [selectedFleet?.unid]);

  // Initial page load:
  // fetch heating/cooling set points + num units acknowledging them
  // load current limit data + num units acknoledging limit
  // fetch unit count
  useEffect(() => {
    const onPageLoad = async () => {
      if (!selectedFleet?.unid || location.pathname !== "/control") {
        return;
      }
      setLoader(true);
      refreshControlData({
        shouldSetSelectedSetpointLimits: true,
        shouldSetSelectedCurrentLimit: true,
        shouldSetInitialCurrentLimit: true,
        shouldSetInitialControlValues: true,
        shouldSetSelectedControlValues: true,
        shouldSetSelectedSchedule: true,
      });
      try {
        const fleetResponse = await getFleet(selectedFleet.unid);
        if (selectedFleet.unid === selectedUnidRef.current) {
          setNumTotalUnits(fleetResponse?.data?.zone?.unitCount);
        }
      } catch (error) {
        setUnblockedZoneUnid(selectedFleet.unid);
        showToast("Failed to fetch Zone data", "failed");
        console.error("Error fetching fleet data:", error);
      } finally {
        if (selectedFleet.unid === selectedUnidRef.current) {
          setLoader(false);
        }
      }
    }
    onPageLoad();
  }, [selectedFleet?.unid, location.pathname, refreshControlData, showToast]);

  const buildControlPayload = () => ({
    unid: selectedFleet.unid,
    controlValues: { isControlValuesEnabled, powerOn },
  });

  const applyModeAndFan = (payload) => {
    payload.controlValues.mode = selectedMode;
    payload.controlValues.fanSetting = selectedFan || "AUTO";
    setSelectedFan(payload.controlValues.fanSetting);
  };

  const applySetpoints = (payload, heatUpperLimitF = null, coolLowerLimitF = null) => {
    // Resolve actual Fahrenheit values with fallback
    const heatF = heatUpperLimitF ?? heatTargetTempF;
    const coolF = coolLowerLimitF ?? coolTargetTempF;

    if (selectedMode === "HEAT") {
      payload.controlValues.heatTargetTempC = convertFahrenheitToCelsius(heatF);
    }

    if (selectedMode === "COOL" || selectedMode === "DRY") {
      payload.controlValues.coolTargetTempC = convertFahrenheitToCelsius(coolF);
    }

    if (selectedMode === "HEAT_AND_COOL") {
      let finalHeat = heatF;
      let finalCool = coolF;

      // Enforce minimum 5°F separation
      if ((finalCool - finalHeat) < DEFAULT_CONTROL_VALUES.DEAD_BAND_F) {
        // If heat is higher than cool, push cool up else push heat down
        if (finalHeat >= finalCool) {
          finalCool = clamp(
            finalHeat + DEFAULT_CONTROL_VALUES.DEAD_BAND_F,
            finalCool,
            DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F
          );
        } else {
          finalHeat = clamp(
            finalCool - DEFAULT_CONTROL_VALUES.DEAD_BAND_F,
            DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F,
            finalHeat
          );
        }
      }

      payload.controlValues.heatTargetTempC = convertFahrenheitToCelsius(finalHeat);
      payload.controlValues.coolTargetTempC = convertFahrenheitToCelsius(finalCool);
    }
  };

  const ensureSetpointsAvailable = () => {
    if (hasSetpointsRequiredForMode(selectedMode, heatTargetTempF, coolTargetTempF)) {
      return true;
    }
    showToast(SETPOINTS_UNAVAILABLE_MESSAGE, "failed");
    return false;
  };

  const onForceReset = async () => {
    const payload = buildControlPayload();
    if (!isZonePoweredOff) {
      if (!ensureSetpointsAvailable()) {
        return;
      }
      applyModeAndFan(payload);
      applySetpoints(payload);
    }

    if (postControlValuesSaveTimeoutRef.current) {
      clearTimeout(postControlValuesSaveTimeoutRef.current);
    }

    try {
      await saveFleetControlValues(payload);
      trackEvent(AnalyticsSchemas.webDeviceControl, {
        unid: payload.unid,
        control_type: ControlType.forceReset,
        power_state: payload.controlValues.powerOn ?? null,
        op_mode: payload.controlValues.mode ?? null,
        fan_setting: payload.controlValues.fanSetting ?? null,
        heat_target_temp_c: payload.controlValues.heatTargetTempC ?? null,
        cool_target_temp_c: payload.controlValues.coolTargetTempC ?? null,
      });
      showToast("Control Values force reset is successful", "success");
      setNumUnitsSetControlValues(0);
      setInitialControlValues({
        powerOn: payload.controlValues.powerOn,
        mode: payload.controlValues.mode,
        fanSetting: payload.controlValues.fanSetting,
        heatTargetTempF:
          payload.controlValues.heatTargetTempC != null
            ? convertCelsiusToFahrenheit(payload.controlValues.heatTargetTempC)
            : heatTargetTempF,
        coolTargetTempF:
          payload.controlValues.coolTargetTempC != null
            ? convertCelsiusToFahrenheit(payload.controlValues.coolTargetTempC)
            : coolTargetTempF,
      });

      postControlValuesSaveTimeoutRef.current = setTimeout(() => {
        getFleetControlValues({
          shouldSetSelectedControlValues: false,
          shouldSetInitialControlValues: false,
        });
      }, POST_SAVE_CHECK_DELAY_MS);
    } catch (error) {
      showToast("Failed to save control values", "failed");
    }
  };

  const onSave = async () => {
    const payload = buildControlPayload();

    if (isControlValuesEnabled) {
      const missingSelectionMessage = getMissingControlSelectionMessage(powerOn, selectedMode);
      if (missingSelectionMessage) {
        showToast(missingSelectionMessage, "failed");
        return;
      }
      if (!isZonePoweredOff) {
        if (!ensureSetpointsAvailable()) {
          return;
        }
        applyModeAndFan(payload);
        applySetpoints(payload);
      }
      const changedControlTypes = [];
      if (!controlValuesEnabledOnServerRef.current) {
        changedControlTypes.push(ControlType.controlValuesToggle);
      }
      if (powerOn !== initialControlValues?.powerOn) {
        changedControlTypes.push(ControlType.powerStateChange);
      }
      const { mode, fanSetting, heatTargetTempC, coolTargetTempC } = payload.controlValues;
      if (mode != null && mode !== initialControlValues?.mode) {
        changedControlTypes.push(ControlType.modeChange);
      }
      if (fanSetting && fanSetting !== (initialControlValues?.fanSetting ?? "AUTO")) {
        changedControlTypes.push(ControlType.fanSpeedChange);
      }
      const heatChanged =
        heatTargetTempC != null &&
        convertCelsiusToFahrenheit(heatTargetTempC) !== initialControlValues?.heatTargetTempF;
      const coolChanged =
        coolTargetTempC != null &&
        convertCelsiusToFahrenheit(coolTargetTempC) !== initialControlValues?.coolTargetTempF;
      if (heatChanged || coolChanged) {
        changedControlTypes.push(ControlType.temperatureChange);
      }
      if (postControlValuesSaveTimeoutRef.current) {
        clearTimeout(postControlValuesSaveTimeoutRef.current);
      }
      try {
        await saveFleetControlValues(payload);
        controlValuesEnabledOnServerRef.current = true;
        if (changedControlTypes.length > 0) {
          // web_device_control's control_type is a single required enum value (schema
          // version 1-0-0, already deployed and immutable), so when a save changes several
          // controls at once only the highest-priority one is attributed here; the rest
          // are still reflected in this event's field values, just not individually
          // countable by control_type.
          const primaryControlType = changedControlTypes[0];
          trackEvent(AnalyticsSchemas.webDeviceControl, {
            unid: payload.unid,
            control_type: primaryControlType,
            enabled: primaryControlType === ControlType.controlValuesToggle ? true : null,
            power_state: payload.controlValues.powerOn ?? null,
            op_mode: payload.controlValues.mode ?? null,
            fan_setting: payload.controlValues.fanSetting ?? null,
            heat_target_temp_c: payload.controlValues.heatTargetTempC ?? null,
            cool_target_temp_c: payload.controlValues.coolTargetTempC ?? null,
          });
        }
        showToast("Control values saved!", "success");
        setNumUnitsSetControlValues(0);
        setInitialControlValues({
          powerOn: payload.controlValues.powerOn,
          mode: payload.controlValues.mode,
          fanSetting: payload.controlValues.fanSetting,
          heatTargetTempF:
            payload.controlValues.heatTargetTempC != null
              ? convertCelsiusToFahrenheit(payload.controlValues.heatTargetTempC)
              : heatTargetTempF,
          coolTargetTempF:
            payload.controlValues.coolTargetTempC != null
              ? convertCelsiusToFahrenheit(payload.controlValues.coolTargetTempC)
              : coolTargetTempF,
        });
        setHasControlChanges(false);
        resetLastAdjustedRangeBar();
        postControlValuesSaveTimeoutRef.current = setTimeout(() => {
          getFleetControlValues({
            shouldSetSelectedControlValues: false,
            shouldSetInitialControlValues: false,
          });
        }, POST_SAVE_CHECK_DELAY_MS);
      } catch (error) {
        showToast("Failed to save control values", "failed");
      }
    }
  };

  useEffect(() => {
    if (selectedMode === "DRY") {
      setSelectedFan("AUTO");
    }
  }, [selectedMode, setSelectedFan]);

  useEffect(() => {
    if (heatTargetTempF == null) {
      setHeatTargetTempF(fallbackHeatingSetPoint);
    }
    if (coolTargetTempF == null) {
      setCoolTargetTempF(fallbackCoolingSetPoint);
    }
  }, [coolTargetTempF, fallbackCoolingSetPoint, heatTargetTempF, fallbackHeatingSetPoint]);

  const rawHeatSetpointF = heatTargetTempF == null ? fallbackHeatingSetPoint : heatTargetTempF;
  const rawCoolSetpointF = coolTargetTempF == null ? fallbackCoolingSetPoint : coolTargetTempF;
  const { heatF: displayHeatSetpointF, coolF: displayCoolSetpointF } = resolveDisplaySetpoints(
    selectedMode,
    rawHeatSetpointF,
    rawCoolSetpointF,
    fallbackCoolingSetPoint,
    fallbackHeatingSetPoint
  );

  return (
    <>
      {loader || !isSelectedZoneDataReady ? (
        <LoadingModal />
      ) : numTotalUnits === 0 ? (
        <div className="w-full sm:w-3/4 grow flex flex-col justify-center md:w-96 mx-auto">
          <div className="w-44 h-40 mx-auto">
            <img
              src={gotoUnits}
              alt="go to units"
              className="size-full h-auto mix-blend-multiply dark:hidden block"
            />
            <img
              src={gotoUnitsInverse}
              alt="go to units"
              className="size-full h-auto hidden dark:block"
            />
          </div>
          <div>
            <h3 className="subtitle dark:text-white text-center text-blue font-semibold tracking-[0.036px] text-xl leading-5 lg:text-2xl lg:leading-6 mt-4">
              You’re All Set—Almost
            </h3>
            <p className="text-dark-grey dark:text-white text-lg leading-6 font-normal tracking-[0.027px] text-center mt-4">
              You haven’t added any units yet.
            </p>
            <p className="text-dark-grey dark:text-white text-lg leading-6 font-normal tracking-[0.027px] mt-6 text-center">
              Let’s explore and connect your first unit to start using the full features.
            </p>
            <button
              className="bg-theme text-white text-base font-semibold tracking-[-0.32px] leading-5 min-w-36 px-5 mx-auto h-10 rounded-lg mt-6 flex items-center justify-center hover:bg-theme/80 transition-all duration-300 ease-in-out"
              onClick={handleNavigateToUnits}
              data-testid="control-goto-units-button"
            >
              Go to Assets - Manage Units
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="titleAndFilters">
            <div className="flex flex-col gap-3 sm:flex-row justify-between items-start sm:items-center flex-wrap">
              <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="control-title">
                Control
              </h2>
              <Button
                onClick={handleGenerateReport}
                className="bg-theme hover:bg-theme/90 text-white text-base font-semibold leading-5 tracking-[-0.32px] h-11 px-5 rounded-md flex items-center gap-2 shadow-appShadow"
                data-testid="control-generate-report-button"
              >
                <span className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <rect x="4" y="3" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M7 7H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M7 10H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M7 13H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </span>
                <span>Generate Report</span>
              </Button>
            </div>
          </div>
          <div className="mt-6 controlsHolder rounded-md">
            <ProgressBar
              numerator={disconnectedCount}
              denominator={numTotalUnits}
              data-testid="disconnected-units-progress"
              title="Disconnected Units"
              subtitle={numTotalUnits === 1 ? "unit disconnected" : "units disconnected"}
              grey={true}
              percentTextGenerator={(percent) => `${percent}% of units are disconnected`}
              IconComponentClass={DisconnectedIcon}
              className="border border-grey dark:border-darkThemeBorder"
              iconProps={{ className: "text-dark-grey dark:text-white/70" }}
            />
          </div>
          {/* Temperature Controls */}
          <div className="mt-6 temperatureHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-4 lg:p-6">
            <div className="flex justify-between flex-wrap items-center gap-5">
              <div className="flex flex-col gap-1">
                <h4 className="text-dark-grey dark:text-white text-lg leading-5 lg:leading-6 lg:text-xl font-semibold tracking-[0.03px]">
                  Zone Temperature Control
                </h4>
                <p className="text-dark-grey dark:text-white text-sm font-medium leading-5 tracking-[0.021px]">
                  Set temperature thresholds for all units
                </p>
              </div>
              <div className="flex items-center gap-4 justify-between w-full sm:w-auto">
                <div className="bg-[#1E555C] rounded-full p-1 min-w-24 h-10 hidden">
                  <button
                    className={`flex-1 px-4 py-1 rounded-full font-semibold transition-colors duration-200 ${temperatureUnit === "°C"
                      ? "bg-white text-[#1E555C]"
                      : "bg-transparent text-white"
                      }`}
                    onClick={() => setTemperatureUnit("°C")}
                    type="button"
                    aria-pressed={temperatureUnit === "°C"}
                  >
                    °C
                  </button>
                  <button
                    className={`flex-1 px-4 py-1 rounded-full font-semibold transition-colors duration-200 ${temperatureUnit === "°F"
                      ? "bg-white text-[#1E555C]"
                      : "bg-transparent text-white"
                      }`}
                    onClick={() => setTemperatureUnit("°F")}
                    type="button"
                    aria-pressed={temperatureUnit === "°F"}
                  >
                    °F
                  </button>
                </div>

                <button
                  className={`bg-theme h-10 px-5 py-3 rounded-md text-white text-base font-semibold leading-5 tracking-[-0.32px] min-w-36 flex justify-center items-center ${isZoneTempSaveDisabled ? "opacity-40 pointer-events-none" : ""
                    } `}
                  data-testid="zone-temperature-save"
                  disabled={isZoneTempSaveDisabled}
                  onClick={() => {
                    updateHeatingCoolingSetPoints({
                      heatUpperLimitF: zoneHeatingSetPoint,
                      coolLowerLimitF: zoneCoolingSetPoint,
                      coldAlertThresholdF,
                      hotAlertThresholdF,
                      unid: selectedFleet.unid,
                      userConfirmedSetPointsChange: false,
                      userConfirmedAlertThresholdWarning: false,
                    });
                  }}
                >
                  Save
                </button>
              </div>

            </div>
            <ProgressBar
              numerator={numUnitAcknowledgedSetpointLimits}
              denominator={numTotalUnits}
              data-testid="setpoint-compliance-progress"
              title="Unit Compliance Status"
              subtitle={numTotalUnits === 1 ? "unit complying" : "units complying"}
              percentTextGenerator={(percent) => `${percent}% of units following zone set-point limits`}
              IconComponentClass={CheckIcon}
              className="mt-4"
              iconProps={{ className: "text-[#00A653]" }}
            />
            <div className="mt-6 lg:mt-7">
              <div className="flex items-stretch gap-6 flex-wrap sm:flex-nowrap">
                {/* Cooling Set-point */}
                <div className="basis-full sm:basis-1/2 flex flex-col gap-4">
                  <div className="bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                    <div className="flex flex-col gap-2 sm:gap-4">
                      <div className="iconHolder">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 48 48"
                          fill="none"
                        >
                          <path
                            d="M24 4V36M24 44V36M24 36L30 42M24 36L18 42M30 6L24 12L18 6"
                            stroke="#0099FF"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M6.67957 14.0008L13.6078 18.0008M13.6078 18.0008L34.3924 30.0008M13.6078 18.0008L11.4116 9.80469M13.6078 18.0008L5.41162 20.197M34.3924 30.0008L41.3206 34.0008M34.3924 30.0008L42.5885 27.8047M34.3924 30.0008L36.5885 38.197"
                            stroke="#0099FF"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M41.3201 14.0008L34.3919 18.0008M34.3919 18.0008L13.6073 30.0008M34.3919 18.0008L36.588 9.80469M34.3919 18.0008L42.588 20.197M13.6073 30.0008L6.67908 34.0008M13.6073 30.0008L5.41113 27.8047M13.6073 30.0008L11.4111 38.197"
                            stroke="#0099FF"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <div className="flex flex-col gap-1">
                        <h4 className="text-dark-grey dark:text-white text-xl leading-7 lg:leading-7 lg:text-lg font-medium tracking-[0.27px]">
                          Cooling Limit
                        </h4>
                        <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.021px]">
                          The system will not allow the cooling set point of the units in this zone to be lower than this temperature
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 max-lg:flex-wrap">
                      <div
                        className="sliderWrapper relative bg-dark-grey/20 dark:bg-[#F4F4E1]/25 h-[5px] rounded-md flex grow max-lg:w-full"
                        data-testid="cooling-limit-slider"
                        ref={sliderWrapperRefCooling}
                        onMouseDown={(e) => {
                          startDrag(
                            e,
                            sliderWrapperRefCooling,
                            setZoneCoolingSetPoint,
                            "coolLowerLimit"
                          );
                        }}
                        onTouchStart={(e) => {
                          startDrag(
                            e,
                            sliderWrapperRefCooling,
                            setZoneCoolingSetPoint,
                            "coolLowerLimit"
                          );
                        }}
                      >
                        {Number.isFinite(zoneCoolingSetPoint) && (
                          <div
                            className="absolute top-1/2 -translate-y-1/2 z-50"
                            style={{
                              left: `calc(${getSliderPercentFromFahrenheit(zoneCoolingSetPoint, "coolLowerLimit")}% - 12px)`,
                            }}
                          >
                            <div className="size-6 rounded-full border-4 border-[#0099FF] bg-white shadow-sm" />
                          </div>
                        )}
                      </div>
                      <p
                        data-testid="cooling-limit-value"
                        className="progressValue basis-[104px] sm:basis-[120px] max-lg:grow whitespace-nowrap grow-0 shrink-0 text-[#0099FF] text-3xl sm:text-[32px] font-semibold leading-7 tracking-[0.048px] text-right">
                        {!Number.isFinite(zoneCoolingSetPoint)
                          ? "--"
                          : temperatureUnit === "°C" ? convertFahrenheitToCelsius(zoneCoolingSetPoint).toFixed(0) + "°C" : zoneCoolingSetPoint.toFixed(0) + "°F"}
                      </p>
                    </div>
                  </div>
                  <div className={`relative bg-white dark:bg-transparent rounded-lg border border-grey p-4 ${!isHotAlertEnabled ? "opacity-50" : ""}`}>
                    <div className="absolute left-4 top-4">
                      <AlertIcon severity="warning" size="large" />
                    </div>
                    <div className="flex items-center justify-end gap-4">
                        <button
                          type="button"
                          onClick={handleHotAlertThresholdToggle}
                          disabled={isAccessRestricted}
                          aria-pressed={isHotAlertEnabled}
                          className={`relative w-20 h-10 rounded-full transition-colors duration-300 ${
                            isHotAlertEnabled ? "bg-orange-500" : "bg-gray-300"
                          } ${isAccessRestricted ? "opacity-50 cursor-not-allowed" : ""}`}
                          data-testid="alert-warmer-toggle"
                        >
                          <span className={`absolute left-3 top-3 text-white text-sm font-semibold transition-opacity duration-300 ${isHotAlertEnabled ? "opacity-100" : "opacity-0"}`}>ON</span>
                          <span className={`absolute right-3 top-3 text-gray-700 text-sm font-semibold transition-opacity duration-300 ${isHotAlertEnabled ? "opacity-0" : "opacity-100"}`}>OFF</span>
                          <div className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full shadow-md transform transition-transform duration-300 ${isHotAlertEnabled ? "translate-x-10" : "translate-x-0"}`} />
                        </button>
                    </div>
                    <div className={`mt-3 ${!isHotAlertEnabled ? "pointer-events-none" : ""}`}>
                      <p className="text-dark-grey dark:text-white text-base font-semibold leading-5 tracking-[0.021px]">Alert if Warmer</p>
                      <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.021px] mt-1">
                        The system will create an alert if the space temperature of a unit in this zone is warmer than this temperature
                      </p>
                      <div className="flex items-center gap-6 max-lg:flex-wrap mt-4">
                        <div
                          className="sliderWrapper relative bg-dark-grey/20 dark:bg-[#F4F4E1]/25 h-[5px] rounded-md flex grow max-lg:w-full"
                          data-testid="alert-warmer-slider"
                          ref={sliderWrapperRefHotAlert}
                          onMouseDown={(e) => {
                            if (!isHotAlertEnabled || isAccessRestricted) return;
                            startDrag(e, sliderWrapperRefHotAlert, setHotAlertThresholdValue, "hotAlertThreshold");
                          }}
                          onTouchStart={(e) => {
                            if (!isHotAlertEnabled || isAccessRestricted) return;
                            startDrag(e, sliderWrapperRefHotAlert, setHotAlertThresholdValue, "hotAlertThreshold");
                          }}
                        >
                          {isHotAlertEnabled && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 z-50"
                              style={{ left: getThresholdMarkerLeft(hotAlertThresholdPercent) }}
                            >
                              <div className="size-6 rounded-full border-4 border-[#FE5B26] bg-white shadow-sm" />
                            </div>
                          )}
                        </div>
                        <p className="progressValue basis-[104px] sm:basis-[120px] max-lg:grow whitespace-nowrap grow-0 shrink-0 text-[#FE5B26] text-3xl sm:text-[32px] font-semibold leading-7 tracking-[0.048px] text-right" data-testid="alert-warmer-value">
                          {hotAlertThresholdF === null
                            ? "--"
                            : temperatureUnit === "°C"
                              ? `${convertFahrenheitToCelsius(hotAlertThresholdF).toFixed(0)}°C`
                              : `${hotAlertThresholdF?.toFixed(0)}°F`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Heating Set-point */}
                <div className="basis-full sm:basis-1/2 flex flex-col gap-4">
                  <div className="bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                    <div className="flex flex-col gap-2 sm:gap-4">
                      <div className="iconHolder">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 48 48"
                          fill="none"
                        >
                          <circle
                            cx="24"
                            cy="24"
                            r="10"
                            stroke="#FE5B26"
                            strokeWidth="3"
                          />
                          <path
                            d="M24 4V8"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M24 40V44"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M8 24L4 24"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M44 24L40 24"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M39.5557 8.44531L35.1116 12.5085"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M8.44434 8.44531L12.8884 12.5085"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M12.8887 35.1113L8.44423 39.5558"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          <path
                            d="M39.5557 39.5547L35.1116 35.1101"
                            stroke="#FE5B26"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <div className="flex flex-col gap-1">
                        <h4 className="text-dark-grey dark:text-white text-xl leading-7 lg:leading-7 lg:text-lg font-medium tracking-[0.27px]">
                          Heating Limit
                        </h4>
                        <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.021px]">
                          The system will not allow the heating set point of the units in this zone to be higher than this temperature
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 max-lg:flex-wrap">
                      <div
                        className="sliderWrapper relative bg-dark-grey/20 dark:bg-[#F4F4E1]/25 h-[5px] rounded-md flex grow max-lg:w-full"
                        data-testid="heating-limit-slider"
                        ref={sliderWrapperRefHeating}
                        onMouseDown={(e) => {
                          startDrag(
                            e,
                            sliderWrapperRefHeating,
                            setZoneHeatingSetPoint,
                            "heatUpperLimit"
                          );
                        }}
                        onTouchStart={(e) => {
                          startDrag(
                            e,
                            sliderWrapperRefHeating,
                            setZoneHeatingSetPoint,
                            "heatUpperLimit"
                          );
                        }}
                      >
                        {Number.isFinite(zoneHeatingSetPoint) && (
                          <div
                            className="absolute top-1/2 -translate-y-1/2 z-50"
                            style={{
                              left: `calc(${getSliderPercentFromFahrenheit(zoneHeatingSetPoint, "heatUpperLimit")}% - 12px)`,
                            }}
                          >
                            <div className="size-6 rounded-full border-4 border-[#FE5B26] bg-white shadow-sm" />
                          </div>
                        )}
                      </div>
                      <p
                        data-testid="heating-limit-value"
                        className="progressValue basis-[104px] sm:basis-[120px] max-lg:grow whitespace-nowrap grow-0 shrink-0 text-[#FE5B26] text-3xl sm:text-[32px] font-semibold leading-7 tracking-[0.048px] text-right">
                        {!Number.isFinite(zoneHeatingSetPoint)
                          ? "--"
                          : temperatureUnit === "°C" ? convertFahrenheitToCelsius(zoneHeatingSetPoint).toFixed(0) + "°C" : zoneHeatingSetPoint.toFixed(0) + "°F"}
                      </p>
                    </div>
                  </div>
                  <div className={`relative bg-white dark:bg-transparent rounded-lg border border-grey p-4 ${!isColdAlertEnabled ? "opacity-50" : ""}`}>
                    <div className="absolute left-4 top-4">
                      <AlertIcon severity="warning" size="large" />
                    </div>
                    <div className="flex items-center justify-end gap-4">
                        <button
                          type="button"
                          onClick={handleColdAlertThresholdToggle}
                          disabled={isAccessRestricted}
                          aria-pressed={isColdAlertEnabled}
                          className={`relative w-20 h-10 rounded-full transition-colors duration-300 ${
                            isColdAlertEnabled ? "bg-orange-500" : "bg-gray-300"
                          } ${isAccessRestricted ? "opacity-50 cursor-not-allowed" : ""}`}
                          data-testid="alert-colder-toggle"
                        >
                          <span className={`absolute left-3 top-3 text-white text-sm font-semibold transition-opacity duration-300 ${isColdAlertEnabled ? "opacity-100" : "opacity-0"}`}>ON</span>
                          <span className={`absolute right-3 top-3 text-gray-700 text-sm font-semibold transition-opacity duration-300 ${isColdAlertEnabled ? "opacity-0" : "opacity-100"}`}>OFF</span>
                          <div className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full shadow-md transform transition-transform duration-300 ${isColdAlertEnabled ? "translate-x-10" : "translate-x-0"}`} />
                        </button>
                    </div>
                    <div className={`mt-3 ${!isColdAlertEnabled ? "pointer-events-none" : ""}`}>
                      <p className="text-dark-grey dark:text-white text-base font-semibold leading-5 tracking-[0.021px]">Alert if Colder</p>
                      <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.021px] mt-1">
                        The system will create an alert if the space temperature of a unit in this zone is colder than this temperature
                      </p>
                      <div className="flex items-center gap-6 max-lg:flex-wrap mt-4">
                        <div
                          className="sliderWrapper relative bg-dark-grey/20 dark:bg-[#F4F4E1]/25 h-[5px] rounded-md flex grow max-lg:w-full"
                          data-testid="alert-colder-slider"
                          ref={sliderWrapperRefColdAlert}
                          onMouseDown={(e) => {
                            if (!isColdAlertEnabled || isAccessRestricted) return;
                            startDrag(e, sliderWrapperRefColdAlert, setColdAlertThresholdValue, "coldAlertThreshold");
                          }}
                          onTouchStart={(e) => {
                            if (!isColdAlertEnabled || isAccessRestricted) return;
                            startDrag(e, sliderWrapperRefColdAlert, setColdAlertThresholdValue, "coldAlertThreshold");
                          }}
                        >
                          {isColdAlertEnabled && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 z-50"
                              style={{ left: getThresholdMarkerLeft(coldAlertThresholdPercent) }}
                            >
                              <div className="size-6 rounded-full border-4 border-[#0099FF] bg-white shadow-sm" />
                            </div>
                          )}
                        </div>
                        <p className="progressValue basis-[104px] sm:basis-[120px] max-lg:grow whitespace-nowrap grow-0 shrink-0 text-[#0099FF] text-3xl sm:text-[32px] font-semibold leading-7 tracking-[0.048px] text-right" data-testid="alert-colder-value">
                          {coldAlertThresholdF === null
                            ? "--"
                            : temperatureUnit === "°C"
                              ? `${convertFahrenheitToCelsius(coldAlertThresholdF).toFixed(0)}°C`
                              : `${coldAlertThresholdF?.toFixed(0)}°F`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Zone Override Control */}
          <div className="mt-6 zoneControlHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-4 lg:p-6">
            <div className="flex justify-between flex-wrap items-center gap-5">
              <div className="flex flex-col gap-1">
                <h4 className="text-dark-grey dark:text-white text-lg leading-5 lg:leading-6 lg:text-xl font-semibold tracking-[0.03px]">
                  Zone Override Control
                </h4>
                <p className="text-dark-grey dark:text-white text-sm font-medium leading-5 tracking-[0.021px]">
                  Set power, mode, temperature, and fan speed across units.
                </p>
              </div>
              <div className="flex items-center gap-4 justify-between w-full sm:w-auto">
                <div className="bg-[#1E555C] rounded-full p-1 min-w-24 h-10 hidden">
                  <button
                    className={`flex-1 px-4 py-1 rounded-full font-semibold transition-colors duration-200 ${temperatureUnit === "°C"
                        ? "bg-white text-[#1E555C]"
                        : "bg-transparent text-white"
                      }`}
                    onClick={() => setTemperatureUnit("°C")}
                    type="button"
                    aria-pressed={temperatureUnit === "°C"}
                  >
                    °C
                  </button>
                  <button
                    className={`flex-1 px-4 py-1 rounded-full font-semibold transition-colors duration-200 ${temperatureUnit === "°F"
                        ? "bg-white text-[#1E555C]"
                        : "bg-transparent text-white"
                      }`}
                    onClick={() => setTemperatureUnit("°F")}
                    type="button"
                    aria-pressed={temperatureUnit === "°F"}
                  >
                    °F
                  </button>
                </div>
                <div className="flex items-center justify-center">
                  <button
                    data-testid="control-values-toggle"
                    onClick={() => {
                      if (isControlToggleDisabled) return;
                      handleControlValuesToggle();
                    }}
                    disabled={isControlToggleDisabled}
                    className={`relative w-20 h-10 rounded-full transition-colors duration-300 ${isControlValuesEnabled ? "bg-orange-500" : "bg-gray-300"
                      } ${isControlToggleDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span
                      className={`absolute left-3 top-3 text-white text-sm font-semibold transition-opacity duration-300 ${isControlValuesEnabled ? "opacity-100" : "opacity-0"
                        }`}
                    >
                      ON
                    </span>
                    <span
                      className={`absolute right-3 top-3 text-gray-700 text-sm font-semibold transition-opacity duration-300 ${isControlValuesEnabled ? "opacity-0" : "opacity-100"
                        }`}
                    >
                      OFF
                    </span>
                    <div
                      className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full shadow-md transform transition-transform duration-300 ${isControlValuesEnabled ? "translate-x-10" : "translate-x-0"
                        }`}
                    />
                  </button>
                </div>
                <p>Control Units from Nexus</p>
              </div>
            </div>
            <div className="mt-6 zoneControlHolder dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-4 lg:p-6 bg-gray-100">
              {/* Header */}
              <div className="flex justify-between flex-wrap items-center gap-5">
                <div className="flex flex-col gap-1">
                  <h4 className="text-dark-grey dark:text-white text-lg leading-5 lg:leading-6 lg:text-xl font-semibold tracking-[0.03px]">
                    Setting Configuration
                  </h4>
                </div>

                <div className="flex items-center gap-4 justify-between w-full sm:w-auto">
                  <button
                    className={`h-10 px-5 py-3 rounded-md text-[#f97316] bg-white border-orange-500 border-2 text-base font-semibold leading-5 tracking-[-0.32px] min-w-36 flex justify-center items-center ${isForceResetDisabled ? "opacity-40 pointer-events-none" : "opacity-100 cursor-pointer"
                      }`}
                    data-testid="control-values-force-reset"
                    disabled={isForceResetDisabled}
                    onClick={() => {
                      onForceReset();
                    }}
                  >
                    Force Reset
                  </button>

                  <button
                    className={`bg-theme h-10 px-5 py-3 rounded-md text-white text-base font-semibold leading-5 tracking-[-0.32px] min-w-36 flex justify-center items-center ${isControlSaveDisabled ? "opacity-40 pointer-events-none" : "opacity-100 cursor-pointer"
                      }`}
                    data-testid="control-values-save"
                    disabled={isControlSaveDisabled}
                    onClick={() => {
                      onSave();
                    }}
                  >
                    Save
                  </button>

                </div>
              </div>
              {isControlValuesEnabled && (<ProgressBar
                numerator={numUnitsSetControlValues}
                denominator={numTotalUnits}
                data-testid="control-values-compliance-progress"
                title="Control Values Compliance Status"
                subtitle={numTotalUnits === 1 ? "unit acknowledging" : "units acknowledging"}
                percentTextGenerator={(percent) => `${percent}% of units acknowledged zone control values`}
                IconComponentClass={CheckIcon}
                className="mt-4"
                iconProps={{ className: "text-[#00A653]" }}
              />)}
              {/* Power */}
              <div
                className={`mt-6 bg-white dark:bg-transparent rounded-lg border border-grey p-6 ${!isControlValuesEnabled
                    ? "opacity-50 pointer-events-none"
                    : "opacity-100"
                  }`}
              >
                <div className="flex flex-col gap-2 sm:gap-4">
                  <div className="font-semibold text-[#1f555c] dark:text-white">
                    <h4>Power</h4>
                  </div>
                  <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.021px]">
                    Optionally force every unit in this zone to power on or off. Click the highlighted option to deselect it
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-6 justify-center">
                  {[{ value: true, label: "Power On" }, { value: false, label: "Power Off" }].map((option) => {
                    const isSelected = powerOn === option.value;
                    return (
                      <Button
                        key={option.label}
                        type="button"
                        aria-pressed={isSelected}
                        data-testid={`zone-power-${option.value ? "on" : "off"}`}
                        disabled={isAccessRestricted}
                        onClick={() => setPowerOn(togglePowerOnSelection(powerOn, option.value))}
                        className={`flex items-center justify-center gap-3 min-w-40 h-14 px-6 rounded-lg border-2 text-base font-semibold tracking-[-0.32px] transition-colors duration-200 ${isSelected
                            ? "bg-theme border-theme text-white"
                            : "bg-white dark:bg-transparent border-grey text-dark-grey dark:text-white"
                          } ${isAccessRestricted ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                          }`}
                      >
                        <Icon
                          src={isSelected ? powerSymbolInverted : powerSymbol}
                          className="size-6"
                          alt=""
                        />
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
              {/* Three Panels Side-by-Side */}
              <div
                data-testid="zone-control-settings-panels"
                className={`flex flex-col lg:flex-row gap-6 mt-6 justify-center items-stretch ${areSettingsDisabled
                    ? "opacity-50 pointer-events-none"
                    : "opacity-100 cursor-pointer"
                  }`}
              >
                {/* Panel 1: Mode */}
                <div className="flex-1 bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                  <div className="flex flex-col gap-2 sm:gap-4">
                    <div className="font-semibold text-[#1f555c] dark:text-white" >
                      <h4>Mode</h4>
                    </div>
                  </div>
                  <UnitControlMenu
                    title="mode"
                    menuOptions={MODE_OPTIONS}
                    selectedOption={selectedMode}
                    setSelectedOption={setSelectedMode}
                  />
                </div>

                {/* Panel 2: Setpoint */}
                <div className="flex-1 bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                  <div className="flex flex-col gap-2 sm:gap-4">
                    <div className="font-semibold text-[#1f555c] dark:text-white">
                      <h4>Setpoint</h4>
                    </div>
                  </div>
                  <div className="mt-4">
                    <CircularSetpointControl
                      heatMin={DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F}
                      heatMax={fallbackHeatingSetPoint}
                      heatValue={displayHeatSetpointF}
                      onHeatChange={safeSetHeatTargetTempF}
                      coolMin={fallbackCoolingSetPoint}
                      coolMax={DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F}
                      coolValue={displayCoolSetpointF}
                      onCoolChange={safeSetCoolTargetTempF}
                      controlMode={selectedMode}
                    />
                  </div>
                </div>

                {/* Panel 3: Fan */}
                <div className="flex-1 bg-white dark:bg-transparent rounded-lg border border-grey p-6">
                  <div className="flex flex-col gap-2 sm:gap-4">
                    <div className="font-semibold text-[#1f555c] dark:text-white">
                      <h4>Fan</h4>
                    </div>
                  </div>
                  <UnitControlMenu
                    title="fan"
                    menuOptions={FAN_SETTING_OPTIONS}
                    selectedOption={selectedFan}
                    setSelectedOption={setSelectedFan}
                    selectedMode={selectedMode}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Current Limit Control */}
          <div className="mt-6 currentLimitHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-4 lg:p-6">
            <div className="flex justify-between flex-wrap items-start gap-5">
              <div className="flex flex-col gap-1">
                <h4 className="text-dark-grey dark:text-white text-lg leading-5 lg:leading-6 lg:text-xl font-semibold tracking-[0.03px]">
                  Zone Current Limit
                </h4>
                <p className="text-dark-grey dark:text-white text-sm font-medium leading-5 tracking-[0.021px]">
                  Select the amperage cap applied to every unit in this zone
                </p>
              </div>
              <Button
                className={`bg-theme h-10 px-5 py-3 rounded-md text-white text-base font-semibold leading-5 tracking-[-0.32px] min-w-28 flex justify-center items-center ${isCurrentLimitSaveDisabled ? "opacity-40 pointer-events-none" : ""
                  }`}
                disabled={isCurrentLimitSaveDisabled}
                onClick={() => setIsDisplayingConfirmCurrentLimitModal(true)}
                data-testid="current-limit-save"
              >
                Save
              </Button>
            </div>
            <ProgressBar
              numerator={numUnitsSetCurrentLimit}
              denominator={numTotalUnits}
              data-testid="current-limit-compliance-progress"
              title="Unit Compliance Status"
              subtitle={numTotalUnits === 1 ? "unit complying" : "units complying"}
              percentTextGenerator={(percent) => `${percent}% of units following zone current limit`}
              IconComponentClass={CheckIcon}
              className="mt-4"
              iconProps={{ className: "text-[#00A653]" }}
            />
            <div className="mt-4 flex flex-wrap gap-6 justify-center">
              <div className="flex flex-col gap-3 min-w-[12rem] items-center text-center">
                <span className="text-dark-grey dark:text-white text-base font-semibold leading-6 tracking-[0.021px]">
                  Current Limit
                </span>
                <div className="flex rounded-full border border-[#FF9500] divide-x divide-[#FF9500] overflow-hidden min-w-[220px] w-full max-w-sm mx-auto">
                  {CURRENT_LIMIT_OPTIONS.map((option) => {
                    const isSelected = currentLimit === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isSelected}
                        className={`flex-1 px-4 py-2 text-sm font-semibold tracking-[0.03px] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF9500] ${isSelected
                            ? "bg-[#FF9500] text-white"
                            : "bg-transparent text-[#FF9500]"
                          }`}
                        onClick={() => setCurrentLimit(option.value)}
                        data-testid={`current-limit-option-${option.value}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          {/* Zone Schedule */}
          <div className="mt-6 scheduleHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-4 lg:p-6">
            <div className="flex justify-between flex-wrap items-center gap-5">
              <div className="flex flex-col gap-1">
                <h4 className="text-dark-grey dark:text-white text-lg leading-5 lg:leading-6 lg:text-xl font-semibold tracking-[0.03px]">
                  Zone Schedule
                </h4>
                <p className="text-dark-grey dark:text-white text-sm font-medium leading-5 tracking-[0.021px]">
                  Set a recurring weekly schedule across units.
                </p>
              </div>
              <div className="flex items-center gap-4 justify-between w-full sm:w-auto">
                <button
                  onClick={handleScheduleToggle}
                  disabled={isScheduleToggleDisabled}
                  data-testid="schedule-toggle"
                  className={`relative w-20 h-10 rounded-full transition-colors duration-300 ${isScheduleEnabled ? "bg-orange-500" : "bg-gray-300"
                    } ${isScheduleToggleDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`absolute left-3 top-3 text-white text-sm font-semibold transition-opacity duration-300 ${isScheduleEnabled ? "opacity-100" : "opacity-0"
                      }`}
                  >
                    ON
                  </span>
                  <span
                    className={`absolute right-3 top-3 text-gray-700 text-sm font-semibold transition-opacity duration-300 ${isScheduleEnabled ? "opacity-0" : "opacity-100"
                      }`}
                  >
                    OFF
                  </span>
                  <div
                    className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full shadow-md transform transition-transform duration-300 ${isScheduleEnabled ? "translate-x-10" : "translate-x-0"
                      }`}
                  />
                </button>
                {isScheduleEnabled && (
                  <Button
                    className={`h-10 px-5 py-3 rounded-md text-[#f97316] bg-white border-orange-500 border-2 text-base font-semibold leading-5 tracking-[-0.32px] flex justify-center items-center whitespace-nowrap ${isScheduleForceResetDisabled ? "opacity-40 pointer-events-none" : "opacity-100 cursor-pointer"
                      }`}
                    data-testid="schedule-force-reset"
                    disabled={isScheduleForceResetDisabled}
                    onClick={handleForceResetSchedule}
                  >
                    Force Reset
                  </Button>
                )}
                <Button
                  className={`size-10 shrink-0 rounded-full bg-orange-500 text-white text-2xl font-semibold leading-none flex justify-center items-center ${isAddScheduleEventDisabled ? "opacity-40 pointer-events-none" : ""
                    }`}
                  disabled={isAddScheduleEventDisabled}
                  onClick={openCreateScheduleEventModal}
                  data-testid="schedule-add-event"
                  aria-label="Add schedule event"
                >
                  +
                </Button>
              </div>
            </div>
            {isScheduleEnabled && (
              <ProgressBar
                numerator={numUnitsSetSchedule}
                denominator={numTotalUnits}
                data-testid="schedule-compliance-progress"
                title="Schedule Compliance Status"
                subtitle={numTotalUnits === 1 ? "unit acknowledging" : "units acknowledging"}
                percentTextGenerator={(percent) => `${percent}% of units acknowledged zone schedule`}
                IconComponentClass={CheckIcon}
                className="mt-4"
                iconProps={{ className: "text-[#00A653]" }}
              />
            )}
            <div className="mt-6 flex flex-col gap-4">
              {scheduleEvents.length === 0 && (
                <p className="text-dark-grey dark:text-white text-sm">
                  No schedule events yet. Add one to get started.
                </p>
              )}
              {SCHEDULE_DAYS_ORDER.map((day) => {
                const dayEvents = scheduleEvents
                  .map((event, index) => ({ event, index }))
                  .filter(({ event }) => event.daysOfWeek?.includes(day))
                  .sort((a, b) => a.event.minuteOfDay - b.event.minuteOfDay);

                if (dayEvents.length === 0) {
                  return null;
                }

                return (
                  <div key={day} className="flex flex-col gap-2">
                    <span className="text-dark-grey dark:text-white text-sm font-semibold">
                      {SCHEDULE_DAY_SHORT_NAMES[day]}
                    </span>
                    <div className="flex flex-col gap-2">
                      {dayEvents.map(({ event, index }) => {
                        const eventModeOption = SCHEDULE_MODE_OPTIONS.find(
                          (option) => option.value === event.eventType
                        );
                        return (
                          <button
                            key={`${day}-${index}`}
                            type="button"
                            data-testid={`schedule-event-row-${day.toLowerCase()}-${index}`}
                            onClick={() => openEditScheduleEventModal(event, index)}
                            className="flex items-center justify-between rounded-full border border-grey dark:border-darkThemeBorder px-5 py-3 text-dark-grey dark:text-white hover:bg-gray-50 dark:hover:bg-white/5"
                          >
                            <span>{formatMinuteOfDayLabel(event.minuteOfDay)}</span>
                            <span className="font-semibold" style={colorStyle(eventModeOption?.color)}>
                              {formatEventSummary(event)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {scheduleEvents.length > 0 && (
                <Button
                  className={`mt-2 bg-critical hover:bg-critical/90 text-white h-10 px-5 py-3 rounded-md text-base font-semibold leading-5 tracking-[-0.32px] min-w-36 flex justify-center items-center self-center ${isAccessRestricted ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => setIsDisplayingClearScheduleModal(true)}
                  disabled={isAccessRestricted}
                  data-testid="schedule-clear"
                >
                  Clear Schedule
                </Button>
              )}
            </div>
          </div>
        </>
      )}
      <ToastMessage
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />
      {isGenerateReportModalOpen && (
        <GenerateReportModal
          isOpen={isGenerateReportModalOpen}
          onClose={() => setIsGenerateReportModalOpen(false)}
          selectedFleet={selectedFleet}
          allPodData={allPodData}
          showToast={showToast}
        />
      )}
      {isDisplayingConfirmCurrentLimitModal && <ConfirmModal
        title="Confirm Current Limit Change"
        subText=""
        confirmMessage={<div>
          <p className="my-7">Modifying the current limit for Zone {selectedFleet?.name} will immediately apply to all associated units and may impact system performance in cold-climate conditions.</p>
          <p className="my-7">⚠️ Warning: You are changing the current limit from {CURRENT_LIMIT_DISPLAY_VALUES[initialCurrentLimit]} to {CURRENT_LIMIT_DISPLAY_VALUES[currentLimit]}.</p>
          <p className="my-7">This adjustment may alter heating capacity, power draw, and overall reliability during low-temperature operation.</p>
        </div>}
        cancelText="Cancel"
        submitText="Apply Change"
        onCancel={() => setIsDisplayingConfirmCurrentLimitModal(false)}
        onSubmit={() => {
          setIsDisplayingConfirmCurrentLimitModal(false);
          saveCurrentLimitSelection();
        }}
      />}
      {isDisplayingConfirmTemperatureLimitsModal && <ConfirmModal
        title="Confirm Temperature Limit Change"
        subText=""
        confirmMessage={<div>
          <p className="my-7">Modifying the temperature limit for Zone {selectedFleet?.name} will immediately apply to all associated units and shall impact the
            heat & cool setpoints configured as part of device control configuration. </p>
          <p className="my-7"> The system shall automatically configure the heating & cooling setpoints
            based on new temperature limits you are about to set.</p>
        </div>}
        cancelText="Cancel"
        submitText="Apply Change"
        onCancel={() => setIsDisplayingConfirmTemperatureLimitsModal(false)}
        onSubmit={() => {
          setIsDisplayingConfirmTemperatureLimitsModal(false);
          updateHeatingCoolingSetPoints({
            heatUpperLimitF: zoneHeatingSetPoint,
            coolLowerLimitF: zoneCoolingSetPoint,
            coldAlertThresholdF,
            hotAlertThresholdF,
            unid: selectedFleet.unid,
            userConfirmedSetPointsChange: true,
            userConfirmedAlertThresholdWarning: true,
          });

        }}
      />}
      {isDisplayingSetpointAlertWarningModal && <ConfirmModal
        title="Warning"
        subText=""
        confirmMessage={<div>
          {getSetpointAlertWarningMessages({
            heatUpperLimitF: zoneHeatingSetPoint,
            coolLowerLimitF: zoneCoolingSetPoint,
            coldAlertThresholdF,
            hotAlertThresholdF,
          }).map((message) => (
            <p key={message} className="my-7">{message}</p>
          ))}
        </div>}
        cancelText="Cancel"
        submitText="Save Anyway"
        onCancel={() => setIsDisplayingSetpointAlertWarningModal(false)}
        onSubmit={() => {
          setIsDisplayingSetpointAlertWarningModal(false);
          updateHeatingCoolingSetPoints({
            heatUpperLimitF: zoneHeatingSetPoint,
            coolLowerLimitF: zoneCoolingSetPoint,
            coldAlertThresholdF,
            hotAlertThresholdF,
            unid: selectedFleet.unid,
            userConfirmedSetPointsChange: false,
            userConfirmedAlertThresholdWarning: true,
          });
        }}
      />}
      {isScheduleEventModalOpen && (
        <ScheduleEventModal
          event={editingScheduleEvent?.event ?? null}
          eventIndex={editingScheduleEvent?.index}
          existingEvents={scheduleEvents}
          fallbackHeatingSetPoint={fallbackHeatingSetPoint}
          fallbackCoolingSetPoint={fallbackCoolingSetPoint}
          isLastRemainingEvent={scheduleEvents.length === 1}
          isScheduleEnabled={isScheduleEnabled}
          isSaving={isSavingScheduleEvent}
          onCancel={closeScheduleEventModal}
          onSave={handleSaveScheduleEvent}
          onDelete={editingScheduleEvent ? handleDeleteScheduleEvent : undefined}
        />
      )}
      {isDisplayingClearScheduleModal && <ConfirmModal
        title="Clear Schedule"
        subText=""
        confirmMessage={<div>
          <p className="my-7">This permanently deletes every schedule event for Zone {selectedFleet?.name} and clears the schedule from all associated units. This cannot be undone.</p>
        </div>}
        cancelText="Cancel"
        submitText="Clear Schedule"
        onCancel={() => setIsDisplayingClearScheduleModal(false)}
        onSubmit={handleClearSchedule}
      />}
    </>
  );
};

export default Control;
